import Call from "@/components/common/Call";
import { ui } from "@/components/ui";
import { Route as PageRoute } from "@/routes/_app/diaries/$diaryId/observation/$observationId/entry/$entryId/page/$pageIndex";
import { toKebabCase } from "@/services/capitaliseSentence";
import type { StatefulObservationViewT } from "@/types/database/observations";
import { Link } from "@tanstack/react-router";
import dayjs from "dayjs";
import { BellRing } from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import { useEntryValuesStore } from "../../../../inputs/hooks/useEntryValuesStore";
interface Props {
	observation: StatefulObservationViewT & { index?: number };
}

const DueObservationCall = ({ observation }: Props) => {
	// Extract values with defaults to handle null/undefined cases
	const isNextEntryLocked = observation.next_entry?.is_locked ?? false;
	const dueEntriesCount = observation.due_entries?.length ?? 0;
	const { resetEntryValues } = useEntryValuesStore();
	const newEntryId = uuidv4();

	// Only render if there are due entries
	if (dueEntriesCount === 0) {
		return null;
	}

	// Format the due entries as bullets
	const dueEntriesList = observation.due_entries
		?.map((entry) => {
			if (!entry) return null;

			return (
				<li key={entry.due_at || `entry-${Math.random()}`} className="ml-2">
					{entry.label || "Unnamed entry"} - Due{" "}
					{dayjs(entry.due_at).format("MMM D, YYYY")}
				</li>
			);
		})
		.filter(Boolean);

	return (
		<Call
			icon={<BellRing className="animate-big-wiggle" />}
			words={`${observation.observation_name} has ${dueEntriesCount} due ${
				dueEntriesCount === 1 ? "entry" : "entries"
			}`}
			variant="warning"
			size="md"
			button={
				<ui.Button
					type="button"
					data-testid={`observation-button-${toKebabCase(
						observation.observation_name ?? "",
					)}`}
					disabled={isNextEntryLocked}
					size="sm"
					asChild
					variant="warning"
				>
					<Link
						onClick={resetEntryValues}
						to={PageRoute.to}
						params={{
							diaryId: observation.diary_id,
							entryId: newEntryId,
							observationId: observation.observation_id,
							pageIndex: String(1),
						}}
					>
						Next
					</Link>
				</ui.Button>
			}
		>
			<ul className="pl-4 list-disc">{dueEntriesList}</ul>
		</Call>
	);
};

export default DueObservationCall;
